import { Task } from '@/types/tasks';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RegistrationsContext, Source } from './registrations-context';

const REG_NAV_STORAGE_KEY = 'kintsugi_admin_registration_nav_v1';

type PersistedRegistrationNav = {
  source: Source;
  tasks: Task[];
  organizationId: string;
};

const REGISTRATION_DETAIL_PATH = /^\/registrations\/([^/]+)\/([^/]+)$/;

function parseRegistrationDetailPath(pathname: string): { organizationId: string; registrationId: string } | null {
  const m = REGISTRATION_DETAIL_PATH.exec(pathname);
  if (!m) return null;
  const [, organizationId, registrationId] = m;
  return { organizationId, registrationId };
}

export const RegistrationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filteredRegistrations, setFilteredRegistrations] = useState<Task[]>([]);
  const [currentRegistrationId, setCurrentRegistrationId] = useState<string | null>(null);
  const [source, setSource] = useState<Source>(null);

  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const onTaskManagementPage = pathname.startsWith('/kintsugi-registrations');
    const onRegistrationListPage = pathname.startsWith('/registrations/list');
    const onRegistrationDetails = /^\/registrations\/[^/]+\/[^/]+$/.test(pathname);
    const onRegistrationPage = pathname.startsWith('/registrations');

    if (!onTaskManagementPage && !onRegistrationDetails && !onRegistrationListPage && !onRegistrationPage) {
      try {
        sessionStorage.removeItem(REG_NAV_STORAGE_KEY);
      } catch {
        // ignore quota / private mode
      }
      if (source) {
        setSource(null);
      }
      if (filteredRegistrations.length > 0) {
        setFilteredRegistrations([]);
      }
      if (currentRegistrationId) {
        setCurrentRegistrationId(null);
      }
    }
  }, [location.pathname, source, filteredRegistrations, currentRegistrationId]);

  useEffect(() => {
    if (filteredRegistrations.length === 0 || !source) {
      return;
    }
    const organizationId = filteredRegistrations[0]?.organization_id;
    if (!organizationId) {
      return;
    }
    try {
      const payload: PersistedRegistrationNav = { source, tasks: filteredRegistrations, organizationId };
      sessionStorage.setItem(REG_NAV_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // Quota / private mode — nav queue won't persist; warn for production debugging.
      console.warn('[RegistrationsProvider] Could not persist nav queue to sessionStorage', e);
    }
  }, [source, filteredRegistrations]);

  useEffect(() => {
    const detail = parseRegistrationDetailPath(location.pathname);
    if (!detail || filteredRegistrations.length > 0) {
      return;
    }
    try {
      const raw = sessionStorage.getItem(REG_NAV_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as PersistedRegistrationNav;
      if (!saved?.tasks?.length || saved.organizationId !== detail.organizationId) {
        return;
      }
      const inQueue = saved.tasks.some(t => t.registration?.id === detail.registrationId);
      if (!inQueue) {
        return;
      }
      if (saved.source === 'task' || saved.source === 'list') {
        setSource(saved.source);
      } else {
        setSource('list');
      }
      setFilteredRegistrations(saved.tasks);
    } catch {
      // ignore corrupt storage
    }
  }, [location.pathname, filteredRegistrations.length]);

  const getNextRegistration = (currentId: string): Task | null => {
    if (filteredRegistrations.length === 0) return null;

    const current = filteredRegistrations.find(t => t.registration?.id === currentId);
    if (!current) return null;

    let pool: Task[] = filteredRegistrations;
    if (source === 'task') {
      pool = filteredRegistrations.filter(t => t.status === current.status);
    }

    if (pool.length < 2) return null;

    const idx = pool.findIndex(t => t.registration?.id === currentId);
    if (idx === -1) return null;

    const nextIdx = (idx + 1) % pool.length;
    return pool[nextIdx];
  };

  return (
    <RegistrationsContext.Provider
      value={{
        filteredRegistrations,
        currentRegistrationId,
        setFilteredRegistrations,
        setCurrentRegistrationId,
        getNextRegistration,
        source,
        setSource,
      }}
    >
      {children}
    </RegistrationsContext.Provider>
  );
};
